import type { Node as PMNode } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';

import { makeAutoIndexPlugin, makePresenceGate, type TAutoIndexPatch } from '../../../plugins/shared';
import { LITERATURE_CITATION_NODE_NAME } from '../constants';

/** Dispatch a tr with this meta to reset the gate so the next content change triggers reindexing. */
export const LITERATURE_CITATION_RESET_GATE_META = 'literatureCitationResetGate';

/**
 * Stable-id numbering: each `literatureId` keeps the index it was first
 * assigned, independent of its position in the document. Inserting a new
 * citation above an existing `[1]` produces `[2] … [1]`, which is correct
 * for Vancouver-style citations. Renumbering only happens when a literatureId
 * is entirely removed from the doc, in which case the remaining ids compact.
 *
 * Exported so tests can exercise it without a PM state.
 */
export type TCitationOccurrence = {
  literatureId: string | null | undefined;
  currentIndex: number | null;
};

export const computeStableCitationIndices = (items: TCitationOccurrence[]): (number | null)[] => {
  // Smallest existing index wins; first-appearance order breaks ties for new ids without existing indices.
  const idToSortKey = new Map<string, { key: number; order: number }>();
  items.forEach((it, order) => {
    const id = it.literatureId;
    if (!id) return;
    const existing = idToSortKey.get(id);
    const k = it.currentIndex ?? Number.POSITIVE_INFINITY;
    if (!existing) {
      idToSortKey.set(id, { key: k, order });
    } else if (k < existing.key) {
      existing.key = k;
    }
  });

  const assigned = new Map<string, number>();
  Array.from(idToSortKey.entries())
    .sort((a, b) => (a[1].key !== b[1].key ? a[1].key - b[1].key : a[1].order - b[1].order))
    .forEach(([id], i) => assigned.set(id, i + 1));

  return items.map((it) => (it.literatureId ? (assigned.get(it.literatureId) ?? null) : null));
};

type TLiteratureCitationOccurrence = {
  pos: number;
  attrs: Record<string, unknown>;
  literatureId: string;
  currentIndex: number | null;
};

/** Doc-based, so the same pass serves the editor's own doc and a detached whole-document one. */
export const collectCitationOccurrences = (doc: PMNode): TLiteratureCitationOccurrence[] => {
  const occurrences: TLiteratureCitationOccurrence[] = [];
  doc.nodesBetween(0, doc.content.size, (n, pos) => {
    if (n.type.name !== LITERATURE_CITATION_NODE_NAME) return true;
    const literatureId = String(n.attrs.literatureId ?? '');
    if (!literatureId) return true;
    occurrences.push({
      pos,
      attrs: n.attrs,
      literatureId,
      currentIndex: typeof n.attrs.index === 'number' ? n.attrs.index : null,
    });
    return true;
  });
  return occurrences;
};

/**
 * The citations outside the part the editor holds. Both sides are needed, not just what precedes:
 * a repeated citation reuses the number of its FIRST occurrence, which may be below the part.
 */
export type TCitationSeed = { citationsBefore: TCitationOccurrence[]; citationsAfter: TCitationOccurrence[] };

export const collectLiteratureCitationPatches = (
  state: EditorState,
  seed?: TCitationSeed | null
): TAutoIndexPatch[] => {
  const occurrences = collectCitationOccurrences(state.doc);
  const before = seed?.citationsBefore ?? [];
  const after = seed?.citationsAfter ?? [];
  // Numbering is per literatureId and order-sensitive, so the whole document has to take part in the
  // computation even though only the editor's own occurrences can be patched.
  const wanted = computeStableCitationIndices([...before, ...occurrences, ...after]).slice(
    before.length,
    before.length + occurrences.length
  );

  const patches: TAutoIndexPatch[] = [];
  occurrences.forEach((occ, i) => {
    const want = wanted[i];
    if (want != null && occ.currentIndex !== want) {
      patches.push({ pos: occ.pos, attrs: occ.attrs, want });
    }
  });
  return patches;
};

/**
 * Returns literature ids in stable bibliography order (first-seen wins, ties
 * broken by doc order). Shared source of truth for the auto-index plugin and
 * the autosave payload - both must agree on the order that ends up in the
 * saved `literatureIds` array and the PDF bibliography.
 */
export const getOrderedLiteratureIds = (doc: PMNode): string[] => {
  const occurrences = collectCitationOccurrences(doc);
  const indices = computeStableCitationIndices(occurrences);
  const byId = new Map<string, number>();
  occurrences.forEach((occ, i) => {
    const idx = indices[i];
    if (idx == null || byId.has(occ.literatureId)) return;
    byId.set(occ.literatureId, idx);
  });
  return [...byId.entries()].sort((a, b) => a[1] - b[1]).map(([id]) => id);
};

/**
 * `seed` is injected in `useOptions` from the large-document model, and answers with the citations
 * outside the part the editor holds. Absent - a whole document in the editor - this reduces to
 * numbering the editor alone, exactly as before.
 */
const getAutoIndexLiteratureCitationsPlugin = (seed?: () => TCitationSeed | null) =>
  makeAutoIndexPlugin({
    pluginName: 'literature-citation-auto-index',
    nodeName: LITERATURE_CITATION_NODE_NAME,
    resetMeta: LITERATURE_CITATION_RESET_GATE_META,
    collectPatches: (state) => collectLiteratureCitationPatches(state, seed?.()),
    // Presence gate, not count-delta - replacing one citation in-place (same count, different id) must still trigger reindex.
    makeGate: makePresenceGate,
  });

export default getAutoIndexLiteratureCitationsPlugin;
